/*global globalThis*/

import { call } from 'redux-saga/effects';
import { matchPath } from 'react-router-dom';
import { NetworkError } from '@corefront/eva-fe-sdk';
import {
  fromPairs,
  invertObj,
  pathOr,
  pipe,
  reverse,
  map,
  mapObjIndexed,
  values,
  mergeAll,
} from 'ramda';
import { ROUTES } from '../constants/routeTemplates';
import { sendErrorAnalyticsData } from '../services/analytics';

/**
 * Returns promise for all basic data, which required for rendering app
 * @param {Object} service - MicroFrontendService instance
 * @returns {function(): *}
 */
export function getBaseDataFactory(service) {
  let promise = null;

  return function () {
    promise = Promise.all([
      service.getRenderContainer(),
      service.getLanguage(),
      service.getBaseURL(),
      service.getChannel(),
      service.getProxyPrefix(),
      service.getClientId(),
      service.getUserToken(),
      service.getBrand(),
    ]);

    return promise;
  };
}

/**
 * Custom Redux-Saga effect
 * @link https://github.com/redux-saga/redux-saga/issues/1045
 * @param SomeClass - any <class> that should be instantiated.
 * @param args - arguments for <class> constructor
 * @return - instance of a <class>
 */

export const create = (SomeClass, ...args) =>
  call(() => new SomeClass(...args));

/**
 * Check, if pathname
 * @link https://github.com/ReactTraining/react-router/blob/master/packages/react-router/modules/matchPath.js#L28
 * @param args
 * @param args.pathname - Current pathname from location
 * @param args.path - react-router path as regexp
 * @returns {boolean} - mathces or not
 */
export const isPathMatches = (...args) => !!matchPath.apply(null, args);

/**
 * @param {string} pathname - current pathname from location
 * @param {Array.<string>} routes - list of available routes
 * @return {(string|null)}
 */
export const getPageRoute = (pathname, routes) =>
  routes.find((route) =>
    isPathMatches(pathname, { path: route, exact: true })
  ) || null;

export const getLobbyURL = (baseUrl) =>
  `${globalThis.location.origin}${baseUrl}`;

export const addClass = (selector, className) => {
  const element = document.querySelector(selector);
  if (element) {
    element.classList.add(className);
  }
};

export const removeClass = (selector, className) => {
  const element = document.querySelector(selector);
  if (element) {
    element.classList.remove(className);
  }
};

export const createMockArray = (length) => [...new Array(length).keys()];

export const getRoute = (pathname) =>
  getPageRoute(pathname, Object.values(ROUTES));

export const setWindowLocationHref = (url) => (window.location.href = url);

export const replaceWindowLocationHref = (url) => window.location.replace(url);

export const roundSum = (num) =>
  typeof num === 'number' ? num.toFixed(2) : num;

export const sortBySortOverride = (list, overrideList = []) => {
  const hashMap = pipe(reverse, invertObj)(overrideList);

  return [...list].sort(
    (a, b) => pathOr(-1, [b], hashMap) - pathOr(-1, [a], hashMap)
  );
};

/**
 * Creates mapped object with equal values
 * @param {Array<string|number>} channels - array of keys in map
 * @param {any} value - value for each key in map
 */
export const createMapping = (channels, value) =>
  fromPairs(map((channel) => [channel, value], channels));

/**
 * Creates plain mapped config from such structure:
 * { value1: ['key1', 'key2'], value2: ['key3', 'key4'] }
 * Results:
 * { 'key1': value1, 'key2': value1, 'key3': value2, 'key4': value2 }
 * @param mapConfig
 */
export const createConfigMap = pipe(
  mapObjIndexed(createMapping),
  values,
  mergeAll
);

export function getScrollbarWidth() {
  // Creating invisible container
  const outer = document.createElement('div');
  outer.style.visibility = 'hidden';
  outer.style.overflow = 'scroll'; // forcing scrollbar to appear
  outer.style.msOverflowStyle = 'scrollbar'; // needed for WinJS apps
  document.body.appendChild(outer);

  // Creating inner element and placing it in the container
  const inner = document.createElement('div');
  outer.appendChild(inner);

  // Calculating difference between container's full width and the child width
  const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;

  try {
    // Removing temporary elements from the DOM
    outer.parentNode.removeChild(outer);
  } catch (err) {
    console.error(err);
  }

  return scrollbarWidth;
}

const DEFAULT_CHUNK_SIZE = 2;

/**
 * Func for splitting string like "AAUSBYUA"
 * to ["AA", "US", "UA"]
 * @param {string} str
 * @param {number} chunkSize
 * @returns {null | Array} splited string on countries
 */
export const getChunksFromString = (str, chunkSize = DEFAULT_CHUNK_SIZE) => {
  const regexChunk = new RegExp(`.{1,${chunkSize}}`, 'g');
  return str?.match(regexChunk);
};

export const COINS_COUNT = 100;
export const convertCoinsToAmount = (coins) => {
  return typeof coins === 'number' ? coins / COINS_COUNT : coins;
};

export const buildErrorAnalyticsData = (context, error) => {
  return {
    error_code: error?.statusCode ? `${error?.statusCode}` : null,
    error_info: JSON.stringify({
      error: error?.toString?.(),
      context: {
        ...context,
        headers: {
          ...(context?.headers ?? {}),
          Authorization: context?.headers?.Authorization ? true : false,
        },
      },
      stack: JSON.stringify(error?.stack).substring(0, 768),
    }).substring(0, 2047), // max length is 2048
  };
};

export const sdkErrorHandler = (context) => (e) => {
  sendErrorAnalyticsData(buildErrorAnalyticsData(context, e));

  if (e instanceof NetworkError) {
    console.log('NetworkError', e);
    throw e;
  } else {
    console.log('SDK-Error', e);
  }
};
